import { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useAppSelector } from '../../../store/hooks';
import { apiGetExpansions } from '../../../api/expansionApi';
import { apiGetCards } from '../../../api/cardApi';
import { apiGetMyOwnedCards } from '../../../api/userCardApi';
import type { Expansion } from '../../../types/models/expansion';
import type { Card, CardRarity } from '../../../types/models/card';

const storageUrl = process.env.REACT_APP_STORAGE_URL ?? '';
const toUrl = (path: string | null | undefined) =>
  path ? (path.startsWith('http') ? path : storageUrl + path) : null;

const RARITY_COLOR: Record<CardRarity, string> = {
  common: 'rgba(255,255,255,0.5)',
  rare: '#4a9eff',
  epic: '#c27ff5',
  legendary: 'gold',
};

const Container = styled.div`
  padding: 24px 16px 70px;
  min-height: calc(100vh - 70px);
`;

const Title = styled.h1`
  font-size: 22px;
  font-weight: 700;
  color: gold;
  margin: 0 0 4px;
`;

const Subtitle = styled.p`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.4);
  margin: 0 0 20px;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Row = styled.div`
  background-color: #1a1a1a;
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 10px;
  overflow: hidden;
`;

const RowBanner = styled.div`
  height: 130px;
  background-color: #111;
  position: relative;
  overflow: hidden;
`;

const BannerImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  opacity: 0.65;
`;

const BannerPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: rgba(255,255,255,0.1);
`;

const CompleteBadge = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  background-color: gold;
  color: #000;
  font-size: 10px;
  font-weight: 700;
  padding: 2px 7px;
  border-radius: 20px;
`;

const RowBody = styled.div`
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const RowTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
`;

const ExpName = styled.span`
  font-size: 15px;
  font-weight: 700;
  color: #fff;
`;

const ProgressLabel = styled.span`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.5);
`;

const ProgressTrack = styled.div`
  height: 5px;
  background-color: rgba(255, 255, 255, 0.07);
  border-radius: 3px;
  overflow: hidden;
`;

const ProgressFill = styled.div<{ $pct: number; $complete: boolean }>`
  height: 100%;
  width: ${({ $pct }) => $pct}%;
  background-color: ${({ $complete }) => ($complete ? 'gold' : 'rgba(255,215,0,0.6)')};
  border-radius: 3px;
`;

const RarityRow = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const RarityChip = styled.span<{ $color: string }>`
  font-size: 11px;
  color: rgba(255,255,255,0.5);
  display: flex;
  align-items: center;
  gap: 4px;

  &::before {
    content: '';
    display: inline-block;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background-color: ${({ $color }) => $color};
    flex-shrink: 0;
  }
`;

const GuestNote = styled.p`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.35);
  margin: 0 0 20px;
  font-style: italic;
`;

const StatusText = styled.p`
  text-align: center;
  color: rgba(255, 255, 255, 0.4);
  margin-top: 32px;
  font-size: 14px;
`;

const ExpansionsProgressMobile: React.FC = () => {
  const [expansions, setExpansions] = useState<Expansion[]>([]);
  const [allCards, setAllCards] = useState<Card[]>([]);
  const [ownedMap, setOwnedMap] = useState<Map<number, number>>(new Map());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const isAuthenticated = useAppSelector(
    (state) => state.auth.isAuthenticated || state.auth.userLoggedState !== null
  );

  useEffect(() => {
    let isMounted = true;
    const fetchExpansions = apiGetExpansions()
      .then((data) => {
        if (isMounted) {
          const list: Expansion[] = Array.isArray(data) ? data : (data as any)?.data ?? [];
          setExpansions(list);
        }
      })
      .catch(() => { if (isMounted) setError('Errore durante il caricamento.'); });

    const fetchCards = apiGetCards()
      .then((data) => {
        if (isMounted) {
          const list: Card[] = Array.isArray(data) ? data : (data as any)?.data ?? [];
          setAllCards(list);
        }
      })
      .catch(() => {});

    const fetchOwned = isAuthenticated
      ? apiGetMyOwnedCards()
          .then((data) => {
            if (isMounted) {
              const list = Array.isArray(data) ? data : (data as any)?.data ?? [];
              const map = new Map<number, number>();
              list.forEach((e: { card_id: number; quantity: number }) => map.set(e.card_id, e.quantity));
              setOwnedMap(map);
            }
          })
          .catch(() => {})
      : Promise.resolve();

    Promise.all([fetchExpansions, fetchCards, fetchOwned]).finally(() => {
      if (isMounted) setIsLoading(false);
    });

    return () => { isMounted = false; };
  }, [isAuthenticated]);

  const expansionStats = useMemo(() => {
    return expansions.map((exp) => {
      const cards = allCards.filter((c) => c.expansions.some((e) => e.id === exp.id));
      const total = cards.length;
      const owned = cards.filter((c) => ownedMap.has(c.id)).length;
      const pct = total > 0 ? Math.round((owned / total) * 100) : 0;
      const complete = total > 0 && owned === total;
      const byRarity = (['common', 'rare', 'epic', 'legendary'] as CardRarity[])
        .map((r) => ({
          rarity: r,
          owned: cards.filter((c) => c.rarity === r && ownedMap.has(c.id)).length,
          total: cards.filter((c) => c.rarity === r).length,
        }))
        .filter(({ total: t }) => t > 0);
      return { exp, total, owned, pct, complete, byRarity };
    });
  }, [expansions, allCards, ownedMap]);

  return (
    <Container>
      <Title>Espansioni</Title>
      <Subtitle>Progresso di completamento</Subtitle>

      {!isAuthenticated && (
        <GuestNote>Accedi per vedere il tuo progresso.</GuestNote>
      )}

      {isLoading && <StatusText>Caricamento...</StatusText>}
      {error && <StatusText style={{ color: '#dc3545' }}>{error}</StatusText>}
      {!isLoading && !error && expansions.length === 0 && (
        <StatusText>Nessuna espansione disponibile.</StatusText>
      )}

      {!isLoading && !error && expansionStats.length > 0 && (
        <List>
          {expansionStats.map(({ exp, total, owned, pct, complete, byRarity }) => {
            const img = toUrl(exp.expansion_path);
            return (
              <Row key={exp.id}>
                <RowBanner>
                  {img
                    ? <BannerImg src={img} alt={exp.name} />
                    : <BannerPlaceholder>{exp.name}</BannerPlaceholder>
                  }
                  {complete && <CompleteBadge>Completa</CompleteBadge>}
                </RowBanner>

                <RowBody>
                  <RowTop>
                    <ExpName>{exp.name}</ExpName>
                    {total > 0 && (
                      <ProgressLabel>
                        {isAuthenticated ? `${owned}/${total} · ${pct}%` : `${total} carte`}
                      </ProgressLabel>
                    )}
                  </RowTop>

                  {isAuthenticated && total > 0 && (
                    <ProgressTrack>
                      <ProgressFill $pct={pct} $complete={complete} />
                    </ProgressTrack>
                  )}

                  {isAuthenticated && byRarity.length > 0 && (
                    <RarityRow>
                      {byRarity.map(({ rarity, owned: ro, total: rt }) => (
                        <RarityChip key={rarity} $color={RARITY_COLOR[rarity]}>
                          {ro}/{rt}
                        </RarityChip>
                      ))}
                    </RarityRow>
                  )}
                </RowBody>
              </Row>
            );
          })}
        </List>
      )}
    </Container>
  );
};

export default ExpansionsProgressMobile;
